import { Button } from "@blueprintjs/core";
import { RootProps } from "routes";
import { Link } from "react-router-dom";
import { constructPath, findRoute } from "routes";
import { useDispatch } from "react-redux";
import { logoutUser } from "controllers/user/action";
import { useLocation, useNavigate } from "react-router";
import { Node } from "utils/tree";
import { RouteType } from "routes/types";
import { concat } from "lodash";
import { useEffect, useState } from "react";

interface MobileOverlayProps extends RootProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginClick?: () => void;
  onSignUpClick?: () => void;
}

export const isActive = (location: ReturnType<typeof useLocation>, node: Node<RouteType>): boolean =>
  concat([constructPath(node)], ...node.children.map((c) => constructPath(c))).includes(location.pathname);

const hasChildren = (node: Node<RouteType>): boolean => !node.isLeaf();

const findNavRoute = (node: Node<RouteType> | undefined): Node<RouteType> | undefined => {
  while (node && node?.data?.parentId !== 0) {
    node = node?.parent;
  }
  return node;
};

type AnimationState = 'entering' | 'entered' | 'exiting' | 'exited';

export default function MobileOverlay(props: MobileOverlayProps) {
  const { isOpen, onClose, routes, user } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const nodeDash = findRoute(routes, "Dashboard");
  const nodeSettings = findRoute(routes, "AccountSettings");
  const hidden = location.pathname.includes("/dashboard");
  const parent = findNavRoute(props.node);

  const [animationState, setAnimationState] = useState<AnimationState>('exited');
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && animationState === 'exited') {
      setShouldRender(true);
      requestAnimationFrame(() => {
        setAnimationState('entering');
        setTimeout(() => {
          setAnimationState('entered');
        }, 300);
      });
    } else if (!isOpen && animationState === 'entered') {
      setAnimationState('exiting');
      setTimeout(() => {
        setAnimationState('exited');
        setShouldRender(false);
      }, 300);
    }
  }, [isOpen, animationState]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate("/");
    onClose();
  };

  if (!shouldRender) return null;

  const renderNavLinks = () => {
    if (!parent) return null;

    return parent.children
      .filter((section) => section.data && !section.data.hidden && section.data.name !== 'InfoHome')
      .map((section) => {
        if (!hasChildren(section)) {
          return (
            <Link 
              key={section.data?.name} 
              to={constructPath(section)}
              onClick={onClose}
              className="nav-link"
            >
              {section.data?.label}
            </Link>
          );
        }

        return (
          <div key={section.data?.name} className="nav-section">
            <div className="section-label">{section.data?.label}</div>
            <div className="section-links">
              {section.children
                .filter((child) => !child.data?.hidden)
                .map((child) => (
                  <Link 
                    key={child.data?.name}
                    to={constructPath(child)}
                    onClick={onClose}
                    className={`nav-link ${isActive(location, child) ? 'active' : ''}`}
                  >
                    {child.data?.label}
                  </Link>
                ))}
            </div>
          </div>
        );
      });
  };

  return (
    <div className={`mobile-overlay ${animationState}`} onClick={onClose}>
      <div className={`overlay-content ${animationState}`} onClick={(e) => e.stopPropagation()}>
        <div className="overlay-body">
          <nav className="mobile-nav">
            {renderNavLinks()}
          </nav>

          <div className="overlay-footer">
            {user ? (
              <>
                {nodeSettings && (
                  <Link to={constructPath(nodeSettings)} onClick={onClose}>
                    <Button
                      intent="primary"
                      minimal
                      text="Account Settings"
                      icon="user"
                    />
                  </Link>
                )}
                <Button minimal text="Log Out" icon="log-out" intent="primary" onClick={handleLogout} />
                {!hidden && nodeDash && (
                  <Link to={constructPath(nodeDash)} onClick={onClose}>
                    <Button
                      intent="primary"
                      text="Go to evaluation tool"
                      className="header-right--wb"
                    />
                  </Link>
                )}
              </>
            ) : (
              <div className="auth-buttons">
                <Button
                  icon="log-in"
                  minimal
                  intent="primary"
                  text="Log In"
                  onClick={() => {
                    onClose();
                    props.onLoginClick?.();
                  }}
                />
                <Button
                  className="header-right--wb"
                  intent="primary"
                  text="Create Account"
                  icon="new-person"
                  onClick={() => {
                    onClose();
                    props.onSignUpClick?.();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 